import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useTranslation } from "react-i18next";
import { X, Share, MoreVertical, Download } from "lucide-react";

// Public URLs that will replace these once native apps are submitted.
// Until then, the buttons open the install-instructions modal.
const APP_STORE_URL = "";   // e.g. "https://apps.apple.com/app/savnt/id000000000"
const PLAY_STORE_URL = "";  // e.g. "https://play.google.com/store/apps/details?id=com.savnt.app"

function detectPlatform() {
    if (typeof navigator === "undefined") return "desktop";
    const ua = navigator.userAgent || navigator.vendor || "";
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
    if (/android/i.test(ua)) return "android";
    return "desktop";
}

const APPLE_SVG = (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor" aria-hidden="true">
        <path d="M17.05 12.04c0-2.4 1.96-3.55 2.05-3.61-1.12-1.64-2.86-1.86-3.48-1.89-1.48-.15-2.89.87-3.65.87-.76 0-1.91-.85-3.14-.83-1.62.02-3.11.94-3.95 2.39-1.68 2.92-.43 7.25 1.21 9.62.8 1.16 1.76 2.46 3 2.41 1.21-.05 1.67-.78 3.13-.78 1.45 0 1.88.78 3.16.76 1.3-.02 2.13-1.18 2.93-2.35.92-1.35 1.31-2.66 1.33-2.72-.03-.01-2.55-.98-2.59-3.87zM14.66 5.06c.65-.79 1.1-1.89 0.98-2.99-0.94.04-2.09.63-2.77 1.42-.61.7-1.14 1.82-1 2.9.01.07 1.08.13 1.79-.07.71-.2 1.65-.49 1-.26-.65-.23-1-1-1-.99z"/>
    </svg>
);

const GOOGLE_PLAY_SVG = (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <path fill="#FFFFFF" d="M3.609 1.814L13.792 12 3.609 22.186A1.99 1.99 0 0 1 3 20.762V3.238c0-.532.207-1.024.609-1.424z"/>
        <path fill="#FFFFFF" d="M14.5 12.707l2.81-2.81-9.65-5.42L14.5 12.707zM14.5 11.293l-6.84 8.23 9.65-5.42-2.81-2.81z"/>
        <path fill="#FFFFFF" d="M20.187 10.5l-2.844 1.5 2.844 1.5c.66.36.66 1.34 0 1.7l-3.045 1.71L14.5 12.707l2.642-2.703 3.045 1.71c.66.35.66 1.33 0 1.69z"/>
    </svg>
);

const ChromeInstallButton = ({ deferredPrompt, onInstalled }) => {
    if (!deferredPrompt) return null;
    return (
        <button
            data-testid="pwa-install-cta"
            onClick={async () => {
                deferredPrompt.prompt();
                const { outcome } = await deferredPrompt.userChoice;
                if (outcome === "accepted") onInstalled();
            }}
            className="btn-primary w-full mt-4"
        >
            <Download className="h-4 w-4" /> Install SAVNT now
        </button>
    );
};

function InstallModal({ open, onClose }) {
    const [platform, setPlatform] = useState("desktop");
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [installed, setInstalled] = useState(false);

    useEffect(() => {
        if (!open) return;
        setPlatform(detectPlatform());
        setInstalled(false);
    }, [open]);

    useEffect(() => {
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener("beforeinstallprompt", handler);
        return () => window.removeEventListener("beforeinstallprompt", handler);
    }, []);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                data-testid="install-modal"
                className="max-w-md w-[92vw] sm:w-full p-0 bg-[#0C0C0C] border border-white/15 text-[#F4F4F0] rounded-none"
            >
                <button onClick={onClose} className="absolute right-3 top-3 p-1.5 text-[#A3A3A3] hover:text-[#F4F4F0] z-10" aria-label="Close">
                    <X className="h-4 w-4" />
                </button>
                <DialogTitle className="sr-only">Install SAVNT</DialogTitle>
                <DialogDescription className="sr-only">Add SAVNT to your home screen</DialogDescription>
                <div className="p-7 sm:p-9">
                    <div className="text-overline text-[#A3A3A3]">N° 042 — The App</div>
                    <h3 className="text-display text-3xl sm:text-4xl mt-2 leading-tight">
                        Get SAVNT on your <span className="text-display-italic">home screen.</span>
                    </h3>
                    <p className="text-sm text-[#A3A3A3] mt-4 leading-relaxed">
                        Coming to the App Store and Google Play soon. Until then, install the SAVNT app directly — no download, no waiting.
                    </p>

                    {installed && (
                        <div className="mt-5 border border-white/15 p-4 text-sm text-[#9DE0A0]" data-testid="install-success">
                            Installed. Look for the SAVNT icon on your home screen.
                        </div>
                    )}

                    {platform === "ios" && (
                        <div className="mt-6 space-y-4 text-sm text-[#D6D6CC]">
                            <div className="text-overline text-[#666]">iPhone — Safari</div>
                            <ol className="space-y-2.5 list-none">
                                <li className="flex items-start gap-3">
                                    <span className="text-overline text-[#A3A3A3] mt-0.5">01</span>
                                    <span>Tap the <Share className="inline h-4 w-4 mx-1" strokeWidth={1.5} /> Share button at the bottom of Safari.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-overline text-[#A3A3A3] mt-0.5">02</span>
                                    <span>Scroll down and tap <strong className="text-[#F4F4F0]">Add to Home Screen</strong>.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-overline text-[#A3A3A3] mt-0.5">03</span>
                                    <span>Tap <strong className="text-[#F4F4F0]">Add</strong>. The SAVNT icon will appear with the rest of your apps.</span>
                                </li>
                            </ol>
                        </div>
                    )}

                    {platform === "android" && (
                        <div className="mt-6 space-y-4 text-sm text-[#D6D6CC]">
                            <div className="text-overline text-[#666]">Android — Chrome</div>
                            {deferredPrompt ? (
                                <p>Tap the button below to install instantly.</p>
                            ) : (
                                <ol className="space-y-2.5 list-none">
                                    <li className="flex items-start gap-3">
                                        <span className="text-overline text-[#A3A3A3] mt-0.5">01</span>
                                        <span>Tap the <MoreVertical className="inline h-4 w-4 mx-1" strokeWidth={1.5} /> menu in the top right of Chrome.</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-overline text-[#A3A3A3] mt-0.5">02</span>
                                        <span>Tap <strong className="text-[#F4F4F0]">Install app</strong> or <strong className="text-[#F4F4F0]">Add to Home screen</strong>.</span>
                                    </li>
                                </ol>
                            )}
                            <ChromeInstallButton deferredPrompt={deferredPrompt} onInstalled={() => setInstalled(true)} />
                        </div>
                    )}

                    {platform === "desktop" && (
                        <div className="mt-6 text-sm text-[#D6D6CC] leading-relaxed">
                            Open <strong className="text-[#F4F4F0]">savnt242.com</strong> on your phone — Safari on iPhone or Chrome on Android — then use your browser's <em>Add to Home Screen</em> option to install.
                            {deferredPrompt && (
                                <ChromeInstallButton deferredPrompt={deferredPrompt} onInstalled={() => setInstalled(true)} />
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export function AppDownloadSection() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const handleStoreClick = (platform) => () => {
        if (platform === "ios" && APP_STORE_URL) {
            window.open(APP_STORE_URL, "_blank", "noopener");
            return;
        }
        if (platform === "android" && PLAY_STORE_URL) {
            window.open(PLAY_STORE_URL, "_blank", "noopener");
            return;
        }
        setOpen(true);
    };

    return (
        <>
            <section className="container-rail py-24 border-t border-white/5">
                <div className="grid grid-cols-12 gap-10 items-center">
                    <div className="col-span-12 md:col-span-6">
                        <div className="text-overline text-[#A3A3A3] mb-4">{t("home.app_label", { defaultValue: "N° 05 — The App" })}</div>
                        <h2 className="text-display text-4xl sm:text-5xl md:text-6xl leading-[0.92] mb-6">
                            {t("home.app_title_1", { defaultValue: "Carry the studio" })}{" "}
                            <span className="text-display-italic">{t("home.app_title_italic", { defaultValue: "in your pocket." })}</span>
                        </h2>
                        <p className="text-[#A3A3A3] text-base leading-relaxed max-w-md">
                            {t("home.app_body", { defaultValue: "The full SAVNT catalogue, your wishlist, and one-tap reorders — installable on iPhone and Android in seconds." })}
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-6 flex flex-col sm:flex-row gap-3 md:justify-end">
                        <button
                            onClick={handleStoreClick("ios")}
                            data-testid="app-store-button"
                            className="group flex items-center gap-4 border border-white/15 hover:border-white/40 px-5 py-3.5 bg-[#0A0A0A] hover:bg-white/[0.03] transition-all"
                            aria-label="Download on the App Store"
                        >
                            <span className="text-[#F4F4F0]">{APPLE_SVG}</span>
                            <span className="flex flex-col items-start leading-tight text-left">
                                <span className="text-[9px] tracking-[0.18em] uppercase text-[#A3A3A3] font-display">Download on the</span>
                                <span className="text-display text-lg text-[#F4F4F0] -mt-0.5">App Store</span>
                            </span>
                        </button>
                        <button
                            onClick={handleStoreClick("android")}
                            data-testid="play-store-button"
                            className="group flex items-center gap-4 border border-white/15 hover:border-white/40 px-5 py-3.5 bg-[#0A0A0A] hover:bg-white/[0.03] transition-all"
                            aria-label="Get it on Google Play"
                        >
                            <span>{GOOGLE_PLAY_SVG}</span>
                            <span className="flex flex-col items-start leading-tight text-left">
                                <span className="text-[9px] tracking-[0.18em] uppercase text-[#A3A3A3] font-display">Get it on</span>
                                <span className="text-display text-lg text-[#F4F4F0] -mt-0.5">Google Play</span>
                            </span>
                        </button>
                    </div>
                </div>
            </section>

            <InstallModal open={open} onClose={() => setOpen(false)} />
        </>
    );
}

export default AppDownloadSection;
